import { useCallback } from "react";
import { Button, Card, Checkbox, Row, Space } from "antd";
import type { CheckboxChangeEvent } from "antd/lib/checkbox";
import type { Moment } from "moment-timezone";
import { useTranslation } from "react-i18next";

import FilterBoxCard from "../../common/Card/FilterBoxCard";
import DateFiltersPicker from "../../common/DateFiltersPicker";
import RadioButtonGroup from "../../common/RadioButtonGroup";
import { DateFiltersWrapper } from "../../common/styledComponents";
import VatPaymentFrequencyFilter from "../../common/TransactionFilters/VatPaymentFrequencyFilter";
import { getBerlinMomentTimezone } from "../../common/helpers";
import { ZmUserStatus } from "../../../types";
import { ZmFilterOptions } from "./types";
import CustomerSearchInput from "@/lib/customerSearch/CustomerSearchInput";

const ZmFilters = ({
  filterOptions,
  datePickerDefaults,
  onFilterChange,
  onLoadAll,
}: {
  filterOptions: ZmFilterOptions;
  datePickerDefaults: {
    defaultYear: string;
    defaultQuarter: string;
    defaultMonth: Moment | null;
  };
  onFilterChange: (updated: Partial<ZmFilterOptions>) => void;
  onLoadAll: () => void;
}) => {
  const { t } = useTranslation("ZmView");
  const {
    email,
    vatPaymentFrequency,
    dateFrom,
    dateTo,
    status,
    missingRecipientVatId,
  } = filterOptions;
  const handleChange = useCallback(
    (updated: Partial<ZmFilterOptions>) => {
      onFilterChange({
        ...filterOptions,
        ...updated,
      });
    },
    [filterOptions, onFilterChange]
  );

  return (
    <Card bordered={false} bodyStyle={{ padding: "0" }}>
      <Row>
        <FilterBoxCard title={t("filters.email")}>
          <Space>
            {/* Follows `email` from outside (URL seed, Load all) and
                resolves Public ID / Account ID first (SMT-1396). */}
            <CustomerSearchInput
              id="zm-email"
              value={email}
              onSelect={(selected) => {
                handleChange({ email: selected || "" });
              }}
              style={{ width: "auto" }}
            />
            <Button
              type="default"
              onClick={(event) => {
                event.preventDefault();
                onLoadAll();
              }}
            >
              {t("filters.loadAll")}
            </Button>
          </Space>
        </FilterBoxCard>
        <VatPaymentFrequencyFilter
          fetchAll
          onChangeHandler={handleChange}
          value={vatPaymentFrequency}
        />
        <FilterBoxCard title={t("filters.dateFilters")}>
          <DateFiltersWrapper data-test="zmDateFilters">
            <DateFiltersPicker
              dateFrom={dateFrom ?? ""}
              dateTo={dateTo ?? ""}
              maxDate={getBerlinMomentTimezone().format()}
              onChange={handleChange}
              showRangePicker={false}
              defaultYear={datePickerDefaults.defaultYear}
              defaultQuarter={datePickerDefaults.defaultQuarter}
              defaultMonth={datePickerDefaults.defaultMonth}
            />
          </DateFiltersWrapper>
        </FilterBoxCard>
        <FilterBoxCard title={t("filters.status")}>
          <RadioButtonGroup
            value={status}
            isReset={status === undefined}
            options={[
              { label: t("filters.all"), value: undefined },
              ...Object.keys(ZmUserStatus).map((value) => ({
                label: t(`status.${value}`),
                value,
              })),
            ]}
            onChange={(value: keyof typeof ZmUserStatus | undefined) => {
              handleChange({
                status: value,
              });
            }}
          />
        </FilterBoxCard>
        <FilterBoxCard title={t("filters.missingRecipientVatId")}>
          <Checkbox
            checked={Boolean(missingRecipientVatId)}
            onChange={(e: CheckboxChangeEvent) => {
              handleChange({ missingRecipientVatId: e.target.checked });
            }}
          >
            {t("filters.onlyMissingVatId")}
          </Checkbox>
        </FilterBoxCard>
      </Row>
    </Card>
  );
};

export default ZmFilters;
